import { z } from 'zod'

import { PlaceFeatureKey } from './places-features.constants'

export const PlaceSaveErrorKindSchema = z.enum([
  'codeExists',
  'integrationNotConfigured',
  'locationsExist',
  'marketShopRestaurant',
  'multipleTicketTypes',
  'packageRoomMissing',
  'parentOff',
  'remoteLockParent',
  'unknown',
])

export const PlaceSaveErrorKind = PlaceSaveErrorKindSchema.enum

export type PlaceSaveErrorKind = z.infer<typeof PlaceSaveErrorKindSchema>

export interface PlaceSaveError {
  kind: PlaceSaveErrorKind
  features: PlaceFeatureKey[]
  provider?: string
  message: string
}

// Note: the backend answers with plain strings, so these patterns mirror its messages
const INTEGRATION_FEATURES: Record<string, PlaceFeatureKey[]> = {
  '2N': ['twoNAvailable'],
  Accentra: [],
  Butterfly: ['bmxInAppCallsAvailable'],
  Dormakaba: ['dormakabaAvailable'],
  Ecobee: ['ecobeeAvailable'],
  'Honeywell Resideo': ['honeywellAvailable'],
  IglooWork: [
    'iglooWorkAvailable',
    'iglooWorkVisitorAccessAvailable',
    'iglooWorkStaffMasterKeyAvailable',
    'iglooWorkCustomPinAvailable',
    'iglooWorkAlgoPinAvailable',
  ],
  Openpath: ['openpathAvailable'],
  'Point Central': ['pointCentralAvailable'],
  RemoteLock: ['remoteLockAvailable'],
  'Salto Space': ['saltoSpaceAvailable'],
  Visionline: ['visionlineAvailable'],
}

const INTEGRATION_PATTERN = /^(.+?) integration is not configured/i
const PARENT_OFF_PATTERN = /^(.+) cannot be enabled while (\w+) is off/i

const toFeatureKeys = (names: string[]) => names.filter(Boolean) as PlaceFeatureKey[]

export const mapPlaceSaveError = (message: string, status?: number): PlaceSaveError => {
  const base = { features: [] as PlaceFeatureKey[], message }

  if (status === 409) {
    return { ...base, kind: PlaceSaveErrorKind.codeExists }
  }

  const integration = INTEGRATION_PATTERN.exec(message)
  if (integration) {
    const provider = integration[1]
    return {
      ...base,
      features: INTEGRATION_FEATURES[provider] ?? [],
      kind: PlaceSaveErrorKind.integrationNotConfigured,
      provider,
    }
  }

  const parentOff = PARENT_OFF_PATTERN.exec(message)
  if (parentOff) {
    return {
      ...base,
      features: toFeatureKeys(parentOff[1].split(',').map((name) => name.trim())),
      kind: PlaceSaveErrorKind.parentOff,
    }
  }

  if (/MarketShopRestaurantAvailable cannot be true/i.test(message)) {
    return {
      ...base,
      features: ['marketShopRestaurantAvailable'],
      kind: PlaceSaveErrorKind.marketShopRestaurant,
    }
  }

  if (/RemoteLock\w*Available cannot be true/i.test(message)) {
    return {
      ...base,
      features: ['remoteLockFobAvailable', 'remoteLockVisitorAccessAvailable'],
      kind: PlaceSaveErrorKind.remoteLockParent,
    }
  }

  if (/already has spaces/i.test(message)) {
    return {
      ...base,
      features: ['amenityLocationAvailable'],
      kind: PlaceSaveErrorKind.locationsExist,
    }
  }

  if (/multiple ticket types/i.test(message)) {
    return {
      ...base,
      features: ['eventTicketsAvailable'],
      kind: PlaceSaveErrorKind.multipleTicketTypes,
    }
  }

  if (/Package Room not found/i.test(message)) {
    return {
      ...base,
      features: ['packageRoomAvailable'],
      kind: PlaceSaveErrorKind.packageRoomMissing,
    }
  }

  return { ...base, kind: PlaceSaveErrorKind.unknown }
}
